import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

const StlViewer = ({ base64Data }) => {
    const mountRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Converte Base64 em ArrayBuffer
    const base64ToArrayBuffer = (base64) => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    };

    useEffect(() => {
        console.log("StlViewer: Iniciando useEffect");

        if (!base64Data) {
            console.log("StlViewer: Base64 não fornecido.");
            setLoading(false);
            setError("Dados STL não fornecidos.");
            return;
        }

        setLoading(true);
        setError(null);

        let renderer = null;
        let controls = null;
        let scene = null;
        let camera = null;
        let mesh = null;
        let geometry = null;
        let material = null;
        let animationFrameId = null;
        let resizeHandler = null;
        let cancelled = false;

        const init = () => {
            if (cancelled) return;

            const currentMount = mountRef.current;

            // Pode rodar antes do ref estar pronto; tenta de novo no próximo frame
            if (!currentMount) {
                console.log("StlViewer: mountRef ainda não disponível, tentando novamente...");
                animationFrameId = requestAnimationFrame(init);
                return;
            }

            console.log("StlViewer: mountRef disponível, inicializando cena 3D...");

            // Limpa qualquer canvas anterior
            while (currentMount.firstChild) {
                currentMount.removeChild(currentMount.firstChild);
            }

            const width =
                currentMount.clientWidth ||
                currentMount.offsetWidth ||
                600;
            const height =
                currentMount.clientHeight ||
                currentMount.offsetHeight ||
                400;

            // Cena
            scene = new THREE.Scene();
            scene.background = new THREE.Color(0xf3f4f6);

            // Câmera
            camera = new THREE.PerspectiveCamera(
                45,
                width / height,
                0.1,
                1000
            );
            camera.position.set(0, 0, 100);

            // Renderer
            renderer = new THREE.WebGLRenderer({ antialias: true });
            renderer.setSize(width, height);
            renderer.setPixelRatio(window.devicePixelRatio || 1);
            currentMount.appendChild(renderer.domElement);

            // Luzes
            const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
            scene.add(ambientLight);

            const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
            directionalLight1.position.set(1, 1, 1);
            scene.add(directionalLight1);

            const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
            directionalLight2.position.set(-1, -0.5, -1);
            scene.add(directionalLight2);

            // Controles
            controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.05;
            controls.rotateSpeed = 0.7;
            controls.zoomSpeed = 0.7;
            controls.panSpeed = 0.5;
            controls.minDistance = 10;
            controls.maxDistance = 400;
            controls.enablePan = true;

            // Carregar STL
            try {
                console.log("StlViewer: Tentando carregar modelo STL...");

                const cleanBase64Data = (base64Data || "").split(',').pop().trim();
                if (!cleanBase64Data) {
                    throw new Error("Dados Base64 vazios após limpeza.");
                }

                const stlBuffer = base64ToArrayBuffer(cleanBase64Data);
                const loader = new STLLoader();
                geometry = loader.parse(stlBuffer);
                geometry.computeVertexNormals();

                material = new THREE.MeshPhysicalMaterial({
                    color: 0xFDF7F0,        // marfim super claro (quase “dente de catálogo”)
                    metalness: 0.35,        // reflete bem as luzes (alto brilho)
                    roughness: 0.15,        // superfície bem lisa, estilo polimento final
                    clearcoat: 1.0,         // camada extra de brilho (como verniz)
                    clearcoatRoughness: 0.05, // highlight forte e definido
                    transmission: 0.2,      // leve translucidez profunda (efeito esmalte)
                    thickness: 1.0,         // dá profundidade à translucidez
                    ior: 1.45,              // índice de refração semelhante ao esmalte real
                    opacity: 1.0,
                    transparent: true
                });

                mesh = new THREE.Mesh(geometry, material);

                // Centralizar e dimensionar
                geometry.computeBoundingBox();
                const boundingBox = geometry.boundingBox;
                const size = new THREE.Vector3();
                boundingBox.getSize(size);
                const maxDimension = Math.max(size.x, size.y, size.z) || 1;

                geometry.center();

                const scaleFactor = 80 / maxDimension;
                mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);

                // Rotação para visualização odontológica
                mesh.rotation.x = -Math.PI / 2;
                mesh.rotation.z = Math.PI;

                scene.add(mesh);

                // Ajuste da câmera
                const distance = maxDimension * 1.5 || 150;
                camera.position.set(distance, distance, distance);
                camera.lookAt(new THREE.Vector3(0, 0, 0));

                controls.target.set(0, 0, 0);
                controls.update();

                console.log("StlViewer: Carregamento concluído com sucesso.");
                setLoading(false);
            } catch (err) {
                console.error("Erro ao carregar modelo STL:", err);
                setError("Erro ao carregar modelo STL. Verifique se o arquivo é válido.");
                setLoading(false);
            }

            // Animação
            const animate = () => {
                if (cancelled || !renderer || !scene || !camera) return;
                controls.update();
                renderer.render(scene, camera);
                animationFrameId = requestAnimationFrame(animate);
            };
            animate();

            // Resize
            resizeHandler = () => {
                if (!currentMount || !renderer || !camera) return;
                const newWidth =
                    currentMount.clientWidth ||
                    currentMount.offsetWidth ||
                    width;
                const newHeight =
                    currentMount.clientHeight ||
                    currentMount.offsetHeight ||
                    height;

                camera.aspect = newWidth / newHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(newWidth, newHeight);
            };

            window.addEventListener('resize', resizeHandler);
        };

        init();

        // Cleanup
        return () => {
            cancelled = true;

            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }

            if (resizeHandler) {
                window.removeEventListener('resize', resizeHandler);
            }

            if (controls) controls.dispose();
            if (geometry) geometry.dispose();
            if (material) material.dispose();

            if (renderer) {
                renderer.dispose();
            }

            const currentMount = mountRef.current;
            if (currentMount && renderer && renderer.domElement && currentMount.contains(renderer.domElement)) {
                try {
                    currentMount.removeChild(renderer.domElement);
                } catch (e) {
                    // ignora
                }
            }
        };
    }, [base64Data]);

    return (
        <div className="relative w-full h-full min-h-[500px]">
            <div
                ref={mountRef}
                className="w-full h-full min-h-[500px]"
            />
            {loading && !error && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-600 bg-white/70">
                    Carregando modelo 3D...
                </div>
            )}
            {error && (
                <div className="absolute inset-0 flex items-center justify-center text-red-600 bg-white/80 text-center px-4">
                    {error}
                </div>
            )}
        </div>
    );
};

export default StlViewer;
